import React from "react";
import { SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";

import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { Calendar,  Info, Minus, Plus, X } from "lucide-react";
import { Separator } from "../ui/separator";
import { useAppDispatch } from "@/store/hooks";
import { addToCart } from "@/store/slices/appSlice";
import parse from "html-react-parser";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "../ui/breadcrumb";
import { Link } from "react-router-dom";
import { Category } from "@/types/AppTypes";
import { ScrollArea } from "../ui/scroll-area";

const ProductSheet = ({
  className,
  productDetails,
}: {
  className?: string;
  productDetails?: any;
}) => {
  const [quantity, setQuantity] = React.useState(1);
  const dispatch = useAppDispatch();

  const increaseQuantity = () => {
    setQuantity((prev) => prev + 1);
  };

  const decreaseQuantity = () => {
    setQuantity((prev) => Math.max(prev - 1, 1));
  };

  const handleAddToCart = () => {
    if (!productDetails) return;
    const productToAdd = { ...productDetails, quantity };
    dispatch(addToCart(productToAdd));
  };

  return (
    <>
      <SheetContent
        className={cn(
          "sm:max-w-md w-full p-6 shadow-lg bg-white data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right data-[state=closed]:duration-200 data-[state=open]:duration-300   inset-y-0 right-0 h-full ",
          className
        )}
      >
        <ScrollArea className="h-full px-3">
        <SheetHeader className="flex flex-col space-y-2 text-center sm:text-left mb-2 gap-0 p-0">
          <SheetTitle className="text-lg font-semibold ">{productDetails?.name}</SheetTitle>
          <SheetDescription className="text-sm text-palette-text ">
          <div className="mb-2">
              <Breadcrumb>
                <BreadcrumbList className="sm:gap-1">
                  {/* Brand */}
                  {productDetails?.brand?.name && (
                    <>
                      <BreadcrumbItem>
                        <BreadcrumbLink className="text-base font-normal hover:underline text-black" asChild>
                          <Link to={`/brand/${productDetails.brand?.id}`}>
                            {productDetails.brand.name}
                          </Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="">
                        <span className="text-gray-500 text-base">/</span>
                      </BreadcrumbSeparator>
                    </>
                  )}

                  {/* Categories */}
                  {Array.isArray(productDetails?.category) && productDetails.category?.length > 0 ? (
                    productDetails.category.map((category: Category, index: number) => (
                      <React.Fragment key={category?.id || index}>
                        {/* Add separator between categories */}
                        {index > 0 && (
                          <BreadcrumbSeparator className="">
                            <span className="text-gray-500 text-base">,</span>
                          </BreadcrumbSeparator>
                        )}
                        <BreadcrumbItem>
                          <BreadcrumbLink className="text-base font-normal hover:underline text-black" asChild>
                            <Link to={`/category/${category?.id}`}>
                              {category?.name}
                            </Link>
                          </BreadcrumbLink>
                        </BreadcrumbItem>
                      </React.Fragment>
                    ))
                  ) : (
                    <BreadcrumbItem>
                      <BreadcrumbLink className="text-base font-normal"></BreadcrumbLink>
                    </BreadcrumbItem>
                  )}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
            {/* {productDetails?.brand?.name + " · " + productDetails?.category?.name} */}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-6">
          <div className="h-64 w-full overflow-hidden bg-white border border-gray-200 rounded-md">
            <img
              src={productDetails?.images?.[0] || "/placeholder.webp"}
              alt={productDetails?.name || "Product Image"}
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <p className="text-lg font-semibold">${productDetails?.price?.toFixed(2) || "0.00"}</p>
          </div>
          <div>
            {/* <div className="flex items-center gap-2 text-sm text-palette-text">
              <Clock className="h-4 w-4 " />
              <span>Last ordered: about 2 months ago</span>
            </div> */}
            <div className="flex items-center gap-2 text-sm text-palette-text mt-1">
              <Calendar className="h-4 w-4" />
              <span>Order date: {productDetails?.lastOrdered || "N/A"}</span>
            </div>
          </div>
          <Separator className="my-4" />
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Info className="h-4 w-4 " />
              <h3 className="font-medium">Description</h3>
            </div>
            <p className="text-sm text-palette-text">
              {parse(productDetails?.description) || "No description available."}
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium">Quantity</span>
            <div className="flex items-center border  rounded-md">
              <Button
                disabled={quantity === 1}
                className="px-2 py-1 rounded-l-md rounded-r-none hover:bg-black/80 hover:text-white bg-transparent text-black" 
                onClick={decreaseQuantity}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-10 text-center">{quantity}</span>
              <Button
                className="px-2 py-1 rounded-r-md rounded-l-none  hover:bg-black/80 hover:text-white bg-transparent text-black" 
                onClick={increaseQuantity}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <Button
            onClick={handleAddToCart}
            className="whitespace-nowrap bg-black hover:bg-black/80 text-white w-full"
          >
            Add Selected to Cart<span className="w-5">({quantity})</span>
          </Button>
          <SheetClose asChild>
            <Button className="whitespace-nowrap bg-white hover:bg-black/80 hover:text-white w-full border border-gray-400/50 text-black">
              <X className="h-4 w-4" /> Close
            </Button>
          </SheetClose>
        </div>
        </ScrollArea>
      </SheetContent>
    </>
  );
};

export default ProductSheet;
